import { createContext, useContext, useEffect, useMemo, useReducer } from 'react'
import data from '../../data/mainCampaign.json'
import { clearState, loadState, saveState } from '../../utils/storage'

const StoreContext = createContext(null)

const initial = {
  campaign: data,
  receiptNo: '',
  fortuneResult: null,
  maxVisitedStep: 1,
  bathCount: 0,
  bathHistory: []
}

function reducer(state, action) {
  switch (action.type) {
    case 'SUBMIT_RECEIPT':
      return {
        ...state,
        receiptNo: action.payload,
        fortuneResult: action.payload === state.receiptNo ? state.fortuneResult : null
      }
    case 'SET_FORTUNE_RESULT':
      return {
        ...state,
        fortuneResult: action.payload || null
      }
    case 'SET_MAX_STEP':
      return {
        ...state,
        maxVisitedStep: Math.max(state.maxVisitedStep || 1, action.payload || 1)
      }
    case 'ADD_BATH':
      if (state.bathCount >= 4) return state
      return {
        ...state,
        bathCount: state.bathCount + 1,
        bathHistory: [
          { id: `bath-${Date.now()}`, at: new Date().toISOString() },
          ...(state.bathHistory || [])
        ].slice(0, 10)
      }
    case 'RESET_BATH':
      return {
        ...state,
        bathCount: 0,
        bathHistory: []
      }
    case 'RESET':
      return { ...initial, campaign: state.campaign }
    default:
      return state
  }
}

export function CampaignProvider({ children }) {
  const persisted = loadState()
  const [state, dispatch] = useReducer(reducer, {
    ...initial,
    ...(persisted || {}),
    bathCount: 0,
    bathHistory: [],
    campaign: data
  })

  useEffect(() => {
    saveState({
      receiptNo: state.receiptNo,
      maxVisitedStep: state.maxVisitedStep
    })
  }, [state.receiptNo, state.maxVisitedStep])

  const value = useMemo(() => {
    return {
      state,
      submitReceipt: (receiptNo) => dispatch({ type: 'SUBMIT_RECEIPT', payload: receiptNo }),
      setFortuneResult: (payload) => dispatch({ type: 'SET_FORTUNE_RESULT', payload }),
      setMaxVisitedStep: (step) => dispatch({ type: 'SET_MAX_STEP', payload: step }),
      addBath: () => dispatch({ type: 'ADD_BATH' }),
      resetBath: () => dispatch({ type: 'RESET_BATH' }),
      resetAll: () => {
        clearState()
        dispatch({ type: 'RESET' })
      }
    }
  }, [state])

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>
}

export function useCampaignStore() {
  const ctx = useContext(StoreContext)
  if (!ctx) throw new Error('useCampaignStore must be used within CampaignProvider')
  return ctx
}
